import { useState } from 'react';
import { AgentPageShell, FormCard, Field, inputCls } from '../../components/AgentPageShell';
import { AgentResult } from '../../components/AgentResult';
import { api, ApiError } from '../../lib/api';
import { formatMillimes, tndStringToMillimes } from '../../lib/money';
import type { InvokeResponseBase } from '../../lib/types';

interface EmpForm {
  employe_id: string;
  full_name: string;
  status: 'salarie' | 'consultant_freelance';
  gross_tnd: string;
}

interface Payslip {
  employe_id: string;
  full_name: string;
  status: 'salarie' | 'consultant_freelance';
  gross_millimes: number;
  cnss_employe_millimes: number;
  cnss_employeur_millimes: number;
  tfp_millimes: number;
  foprolos_millimes: number;
  ras_honoraires_millimes: number;
  net_a_payer_millimes: number;
  total_cout_employeur_millimes: number;
}

interface DrhResult {
  fiscal_period: string;
  payslips: Payslip[];
  totals: Record<string, number>;
  declarations: Array<{ type: string; amount_millimes: number; next_deadline: string }>;
}

const SAMPLES: EmpForm[] = [
  { employe_id: 'E1', full_name: 'Mohamed Ben Salem', status: 'salarie', gross_tnd: '2000.000' },
  { employe_id: 'E2', full_name: 'Fatma Trabelsi', status: 'salarie', gross_tnd: '2500.000' },
  { employe_id: 'C1', full_name: 'Consultant Externe', status: 'consultant_freelance', gross_tnd: '5000.000' },
];

export function DrhCnssPage() {
  const [period, setPeriod] = useState('2026-04');
  const [employes, setEmployes] = useState<EmpForm[]>(SAMPLES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InvokeResponseBase | null>(null);

  function update(i: number, patch: Partial<EmpForm>) {
    setEmployes((p) => p.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  }
  function addEmp() {
    setEmployes((p) => [...p, { employe_id: `E${p.length + 1}`, full_name: '', status: 'salarie', gross_tnd: '0.000' }]);
  }
  function removeEmp(i: number) {
    setEmployes((p) => p.filter((_, idx) => idx !== i));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const payload = {
        fiscal_period: period,
        employes: employes.map((emp) => ({
          employe_id: emp.employe_id,
          full_name: emp.full_name,
          status: emp.status,
          gross_monthly_millimes: tndStringToMillimes(emp.gross_tnd),
        })),
      };
      const res = await api.invokeAgent('drh_cnss', { payload, fiscal_reference_date: `${period}-15` });
      setResponse(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AgentPageShell
      title="DRH/CNSS"
      module="Extension · Phase 4"
      model="Sonnet 4.6"
      description={
        <>
          Paie + déclarations sociales tunisiennes — CNSS employé (9.68%) et employeur (16.57%),
          TFP (2%), Foprolos (1%), RAS honoraires sur consultants (10%).
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormCard title="Période">
          <Field label="Mois (YYYY-MM)" hint="Mois de la paie à calculer">
            <input className={inputCls + ' font-mono'} value={period} onChange={(e) => setPeriod(e.target.value)} required />
          </Field>
        </FormCard>

        <FormCard
          title={`Effectif (${employes.length})`}
          action={
            <button type="button" onClick={addEmp} className="text-xs px-2 py-1 rounded bg-ink text-cream hover:opacity-90">
              + Ajouter
            </button>
          }
        >
          <div className="space-y-3">
            {employes.map((emp, idx) => (
              <div key={idx} className="border border-border rounded p-3 bg-cream/30 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                <Field label="ID"><input className={inputCls + ' font-mono'} value={emp.employe_id} onChange={(e) => update(idx, { employe_id: e.target.value })} required /></Field>
                <Field label="Nom complet"><input className={inputCls} value={emp.full_name} onChange={(e) => update(idx, { full_name: e.target.value })} required /></Field>
                <Field label="Statut">
                  <select className={inputCls} value={emp.status} onChange={(e) => update(idx, { status: e.target.value as EmpForm['status'] })}>
                    <option value="salarie">Salarié</option>
                    <option value="consultant_freelance">Consultant freelance</option>
                  </select>
                </Field>
                <Field label="Brut mensuel (TND)"><input className={inputCls + ' font-mono'} value={emp.gross_tnd} onChange={(e) => update(idx, { gross_tnd: e.target.value })} /></Field>
                {employes.length > 1 && (
                  <button type="button" onClick={() => removeEmp(idx)} className="text-xs text-red-700 hover:text-red-900 self-end mb-1">
                    Supprimer
                  </button>
                )}
              </div>
            ))}
          </div>
        </FormCard>

        <button type="submit" disabled={loading || employes.length === 0} className="btn-primary">
          {loading ? 'Calcul de la paie…' : 'Calculer la paie'}
        </button>
      </form>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}

      {response && (
        <AgentResult
          response={response}
          renderResult={(result) => <DrhResultView result={result as DrhResult} />}
        />
      )}
    </AgentPageShell>
  );
}

function DrhResultView({ result }: { result: DrhResult }) {
  return (
    <div className="space-y-5">
      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Totaux</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
          <Stat label="Brut total" value={formatMillimes(result.totals.total_gross_millimes ?? 0)} />
          <Stat label="Net total" value={formatMillimes(result.totals.total_net_millimes ?? 0)} highlight />
          <Stat label="CNSS employé" value={formatMillimes(result.totals.total_cnss_employe_millimes ?? 0)} />
          <Stat label="CNSS employeur" value={formatMillimes(result.totals.total_cnss_employeur_millimes ?? 0)} />
          <Stat label="TFP" value={formatMillimes(result.totals.total_tfp_millimes ?? 0)} />
          <Stat label="Foprolos" value={formatMillimes(result.totals.total_foprolos_millimes ?? 0)} />
          <Stat label="RAS honoraires" value={formatMillimes(result.totals.total_ras_millimes ?? 0)} />
          <Stat label="Coût employeur total" value={formatMillimes(result.totals.total_cost_millimes ?? 0)} highlight />
        </div>
      </section>

      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Bulletins de paie ({result.payslips.length})</div>
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-taupe border-b border-border">
                <th className="px-2 py-2 text-left">Employé</th>
                <th className="px-2 py-2 text-left">Statut</th>
                <th className="px-2 py-2 text-right">Brut</th>
                <th className="px-2 py-2 text-right">CNSS empl.</th>
                <th className="px-2 py-2 text-right">RAS</th>
                <th className="px-2 py-2 text-right">Net à payer</th>
                <th className="px-2 py-2 text-right">Coût employeur</th>
              </tr>
            </thead>
            <tbody>
              {result.payslips.map((p) => (
                <tr key={p.employe_id} className="border-b border-border/50">
                  <td className="px-2 py-1.5">
                    <div className="font-medium">{p.full_name}</div>
                    <div className="text-xs text-taupe font-mono">{p.employe_id}</div>
                  </td>
                  <td className="px-2 py-1.5 text-xs">
                    <span className={`px-1.5 py-0.5 rounded ${p.status === 'salarie' ? 'bg-emerald-100 text-emerald-900' : 'bg-amber-100 text-amber-900'}`}>
                      {p.status === 'salarie' ? 'Salarié' : 'Consultant'}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{formatMillimes(p.gross_millimes, { withSymbol: false })}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{p.cnss_employe_millimes > 0 ? formatMillimes(p.cnss_employe_millimes, { withSymbol: false }) : '—'}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{p.ras_honoraires_millimes > 0 ? formatMillimes(p.ras_honoraires_millimes, { withSymbol: false }) : '—'}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-bold">{formatMillimes(p.net_a_payer_millimes, { withSymbol: false })}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-taupe">{formatMillimes(p.total_cout_employeur_millimes, { withSymbol: false })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {result.declarations.length > 0 && (
        <section>
          <div className="text-xs uppercase tracking-wider text-taupe mb-2">Déclarations à effectuer</div>
          <ul className="space-y-1 text-sm">
            {result.declarations.map((d, i) => (
              <li key={i} className="p-2 rounded border border-border bg-white flex items-center justify-between">
                <span><strong>{prettyDeclType(d.type)}</strong> — échéance <span className="font-mono">{d.next_deadline}</span></span>
                <span className="font-mono">{formatMillimes(d.amount_millimes)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`p-2 rounded border text-center ${highlight ? 'border-gold bg-gold/10' : 'border-border bg-white'}`}>
      <div className="text-xs uppercase tracking-wider text-taupe">{label}</div>
      <div className={`font-mono text-base ${highlight ? 'font-bold' : ''}`}>{value}</div>
    </div>
  );
}

function prettyDeclType(t: string): string {
  return ({
    cnss_trimestrielle: 'CNSS trimestrielle',
    tfp_mensuelle: 'TFP mensuelle',
    foprolos_mensuelle: 'Foprolos mensuel',
    ras_mensuelle: 'RAS honoraires',
  } as Record<string, string>)[t] ?? t;
}
